import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import PageHeader from '../components/PageHeader';
import DataState from '../components/DataState';
import { useSession } from '../permissions/SessionContext';
import { fetchOwnerActionLogs, fetchOwnerAuditLogs } from '../api/members';
import { colors, layout } from '../design-system/tokens';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const getScopedParams = (session) =>
  session?.user?.role === 'master' ? { storeId: session.store.id } : {};

/**
 * 개인정보 열람 기록 (master 전용).
 * "누가 / 언제 / 어떤 회원의 / 무슨 개인정보를 열람했나" 를 매장 스코프로 보여준다.
 * 개인정보보호법 시행령 제30조 접속기록의 사후 점검 창구.
 */
const AuditLogs = () => {
  const { session } = useSession();
  const [data, setData] = useState(null);
  const [actionData, setActionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // PII 열람 기록 + 액션 실행 원장을 함께 조회 (같은 audit.read 게이트)
      const [pii, actions] = await Promise.all([
        fetchOwnerAuditLogs(getScopedParams(session)),
        fetchOwnerActionLogs(getScopedParams(session)).catch(() => null),
      ]);
      setData(pii);
      setActionData(actions);
    } catch (err) {
      setError(err?.response?.data?.message || '열람 기록을 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    load();
  }, [load]);

  const entries = data?.entries ?? [];
  const actionEntries = actionData?.entries ?? [];

  return (
    <Box>
      <Stack
        direction="row"
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap' }}
      >
        <PageHeader
          title="개인정보 열람 기록"
          description="관리자·점주가 회원 연락처 원문을 열람한 기록이에요. 최근 순으로 보여줍니다."
        />
        <Tooltip title="새로고침" arrow>
          <span>
            <IconButton onClick={load} disabled={loading} aria-label="새로고침">
              <RefreshRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Alert severity="info" sx={{ mb: layout.cardGap }}>
        연락처 원문 열람만 기록됩니다. 목록·상세의 마스킹 값 조회는 기록 대상이 아니에요.
      </Alert>

      <DataState
        loading={loading}
        error={error}
        onRetry={load}
        skeleton={{ rows: 6, cols: 4 }}
        empty={entries.length === 0}
        emptyText="아직 개인정보 열람 기록이 없어요."
      >
        <Paper sx={{ p: 0, overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Table size="small" sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>시각</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>열람자</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>대상 회원</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>동작</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: colors.text.muted }}>
                      {formatDateTime(entry.at)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {entry.adminEmail || '-'}
                      </Typography>
                      {entry.masterQaMode && (
                        <Chip
                          size="small"
                          label="QA 모드"
                          sx={{
                            mt: 0.25,
                            height: 18,
                            fontSize: 10,
                            bgcolor: colors.status.info.bg,
                            color: colors.status.info.fg,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {entry.targetMemberName || '-'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Chip
                        size="small"
                        label={entry.actionLabel}
                        sx={{
                          height: 22,
                          fontSize: 11,
                          fontWeight: 700,
                          bgcolor: colors.status.neutral.bg,
                          color: colors.status.neutral.fg,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DataState>

      {/* 액션 실행 원장 — 문자/결제 같은 side-effect 액션의 사실 기록 (실발송 전이라 미리보기 위주) */}
      {!loading && !error && (
        <Box sx={{ mt: layout.sectionGap }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>
            액션 실행 기록
          </Typography>
          <Typography variant="caption" sx={{ color: colors.text.muted, display: 'block', mb: layout.cardGap }}>
            문자·결제 같은 액션의 미리보기/발송 이력이에요. 쿨다운(재발송 방지)의 근거가 됩니다.
          </Typography>
          {actionEntries.length === 0 ? (
            <Paper sx={{ p: { xs: 2.5, sm: 3 }, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: colors.text.muted }}>
                아직 액션 실행 기록이 없어요.
              </Typography>
            </Paper>
          ) : (
            <Paper sx={{ p: 0, overflow: 'hidden' }}>
              <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <Table size="small" sx={{ minWidth: 640 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>시각</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>실행자</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>액션</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>결과</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>대상</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {actionEntries.map((entry) => (
                      <TableRow key={entry.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap', color: colors.text.muted }}>
                          {formatDateTime(entry.at)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {entry.adminEmail || '-'}
                          </Typography>
                          {entry.masterQaMode && (
                            <Chip
                              size="small"
                              label="QA 모드"
                              sx={{
                                mt: 0.25,
                                height: 18,
                                fontSize: 10,
                                bgcolor: colors.status.info.bg,
                                color: colors.status.info.fg,
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.actionLabel}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Chip
                            size="small"
                            label={entry.statusLabel}
                            sx={{
                              height: 22,
                              fontSize: 11,
                              fontWeight: 700,
                              bgcolor:
                                entry.status === 'sent'
                                  ? colors.status.success.bg
                                  : entry.status === 'failed'
                                    ? colors.status.danger.bg
                                    : colors.status.neutral.bg,
                              color:
                                entry.status === 'sent'
                                  ? colors.status.success.fg
                                  : entry.status === 'failed'
                                    ? colors.status.danger.fg
                                    : colors.status.neutral.fg,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {entry.targetMemberName || `${entry.recipientCount}명`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AuditLogs;
